import { useEffect, useMemo, useState } from "react";
import axios from "../../utils/axiosInstance";
import DailyReportTable from "./DailyReportTable";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const getLast8Days = () => {
  const days = [];
  const today = new Date();

  for (let i = 1; i <= 8; i++) {
    const past = new Date(today);
    past.setDate(today.getDate() - i);

    const year = past.getFullYear();
    const month = past.getMonth() + 1;
    const day = past.getDate();

    days.push({
      year,
      month,
      day,
      label: past.toLocaleDateString("en-US", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
      value: `${year}-${month}-${day}`,
    });
  }

  return days;
};

const DailyPerGroup = ({ group }) => {
  const [devices, setDevices] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const dayOptions = useMemo(() => getLast8Days(), []);
  const [selectedDay, setSelectedDay] = useState(dayOptions[0]?.value);

  // ================== Fetch devices ==================
  useEffect(() => {
    if (!group?.id) return;

    const fetchDevices = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/devices?groupId=${group.id}`,
        );
        setDevices(res.data || []);
      } catch (err) {
        console.error("Failed to fetch devices", err);
      }
    };

    fetchDevices();
  }, [group?.id]);

  // ================== Fetch daily report ==================
  useEffect(() => {
    if (!devices.length || !selectedDay) return;
    fetchDailyReport(selectedDay);
  }, [devices, selectedDay]);

  const fetchDailyReport = async (dayValue) => {
    const [year, month, day] = dayValue.split("-").map(Number);

    try {
      setLoading(true);
      setError(null);

      const payload = {
        units: devices.map((d) => d.id),
      };

      const res = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/reports/precision-report-day/${year}/${month}/${day}`,
        payload,
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        },
      );

      setReportData(res.data.data.response);
    } catch (err) {
      console.error(err);
      setError("Failed to load daily report");
    } finally {
      setLoading(false);
    }
  };

  // ================== Helpers ==================
  const unitMap = useMemo(
    () =>
      devices.reduce((acc, u) => {
        acc[u.id] = u.name;
        return acc;
      }, {}),
    [devices],
  );

  // ================== Normalize backend data ==================
  const normalizedData = useMemo(() => {
    if (!reportData) return [];

    return Object.entries(reportData).map(([unitId, report]) => ({
      unitId,
      unitName: unitMap[unitId] || unitId,
      year: report.year,
      month: report.month,
      day: report.day,
      down: report.down,
      up: report.up,
      customer: report.customer,
      company: report.company,
      shutDowns: report.shutDowns,
    }));
  }, [reportData, unitMap]);

  // ================== Search filter ==================
  const filteredReportData = useMemo(() => {
    const q = searchQuery.toLowerCase();

    return normalizedData.filter((r) => {
      return (
        r.unitName.toLowerCase().includes(q) ||
        r.year.toString().includes(q) ||
        r.month.toString().includes(q) ||
        r.day.toString().includes(q) ||
        r.down.toString().includes(q) ||
        r.up.toString().includes(q) ||
        r.customer.toString().includes(q) ||
        r.company.toString().includes(q) ||
        r.shutDowns.toString().includes(q)
      );
    });
  }, [normalizedData, searchQuery]);

  // ================== Export ==================
  const exportToCSV = () => {
    if (!filteredReportData.length) return;

    const rows = [
      [
        "Unit Name",
        "Year",
        "Month",
        "Day",
        "Stop(Hrs)",
        "Run(Hrs)",
        "Customer(Hrs)",
        "PRC(Hrs)",
        "Shutdowns",
      ],
      ...filteredReportData.map((r) => [
        r.unitName,
        r.year,
        r.month,
        r.day,
        r.down,
        r.up,
        r.customer,
        r.company,
        r.shutDowns,
      ]),
    ];

    const csv = rows.map((r) => r.map((f) => `"${f}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Daily_Report.csv";
    link.click();
  };

  const exportToExcel = () => {
    if (!filteredReportData.length) return;

    const headers = [
      "Unit Name",
      "Year",
      "Month",
      "Day",
      "Stop(Hrs)",
      "Run(Hrs)",
      "Customer(Hrs)",
      "PRC(Hrs)",
      "Shutdowns",
    ];

    const rows = filteredReportData.map((r) => [
      r.unitName,
      r.year,
      r.month,
      r.day,
      r.down,
      r.up,
      r.customer,
      r.company,
      r.shutDowns,
    ]);

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Daily Report");
    XLSX.writeFile(wb, "Daily_Report.xlsx");
  };

  const exportToPDF = () => {
    if (!filteredReportData.length) return;

    const doc = new jsPDF("l", "pt", "a4");
    doc.text("Daily Units Report", 40, 30);

    const headers = [
      [
        "Unit Name",
        "Year",
        "Month",
        "Day",
        "Stop(Hrs)",
        "Run(Hrs)",
        "Customer(Hrs)",
        "PRC(Hrs)",
        "Shutdowns",
      ],
    ];

    const rows = filteredReportData.map((r) => [
      r.unitName,
      r.year,
      r.month,
      r.day,
      r.down,
      r.up,
      r.customer,
      r.company,
      r.shutDowns,
    ]);

    autoTable(doc, { head: headers, body: rows, startY: 50, theme: "grid" });
    doc.save("Daily_Report.pdf");
  };

  // ================== UI ==================
  return (
    <div>
      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="px-3 py-2 w-full sm:w-64 rounded-md border text-white border-gray-300 bg-transparent placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        <div className="flex flex-wrap gap-2">
          <select
            value={selectedDay}
            onChange={(e) => setSelectedDay(e.target.value)}
            className="px-3 py-2 rounded-md border text-white border-gray-300 bg-slate-700 text-sm"
          >
            {dayOptions.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>

          <button
            onClick={exportToCSV}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors w-[60px] sm:w-auto"
          >
            CSV
          </button>
          <button
            onClick={exportToExcel}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors w-[60px] sm:w-auto"
          >
            Excel
          </button>
          <button
            onClick={exportToPDF}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors w-[60px] sm:w-auto"
          >
            PDF
          </button>
        </div>
      </div>

      <DailyReportTable
        reportData={filteredReportData}
        loading={loading}
        error={error}
      />
    </div>
  );
};

export default DailyPerGroup;
